import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { tokens } from '@companycam/slab';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import useCompanyTemplates from '../hooks/useCompanyTemplates';

import TemplatesList from './TemplatesList';

import EmptyStateContainer from 'components/projects/taskList/components/EmptyStateContainer';

const SectionHeader = styled.p`
  color: var(--cc_color_text_nonessential);
  font-weight: 500;
  font-size: var(--cc_size_text_s);
  line-height: 20px;
  margin-left: var(--cc_size_spacing_l);
`;
const TemplatesContainer = styled.div`
  margin-top: var(--cc_size_spacing_l);
`;

const RecentContainer = styled.div`
  margin-bottom: ${tokens.size_spacing_xl}px;
`;

const messages = defineMessages({
  allTemplates: {
    defaultMessage: 'All Templates',
    description: 'Subheading info on all templates displayed',
    id: 'Projects.taskList.templates.all',
  },
  recentTemplates: {
    defaultMessage: 'Recently Used Templates',
    description: 'Templates recently used heading',
    id: 'Projects.taskList.templates.recent',
  },
});

const CompanyTemplates = ({ includeAllTemplates, recentTemplates }) => {
  const { formatMessage } = useIntl();
  const {
    companyTemplates,
    loading,
    error,
    hasNextPage,
    fetchNextPage,
  } = useCompanyTemplates(includeAllTemplates);
  const [ref, inView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 300px 0px',
  });

  useEffect(
    () => {
      if (inView && hasNextPage) {
        fetchNextPage();
      }
    },
    [inView, hasNextPage, fetchNextPage],
  );
  return (
    <>
      {companyTemplates.length > 0 ? (
        <TemplatesContainer>
          {includeAllTemplates && recentTemplates.length ? (
            <RecentContainer>
              <SectionHeader>
                {formatMessage(messages.recentTemplates)}
              </SectionHeader>
              <TemplatesList templates={recentTemplates} />
            </RecentContainer>
          ) : null}
          {includeAllTemplates ? (
            <SectionHeader>
              {formatMessage(messages.allTemplates)}
            </SectionHeader>
          ) : null}
          <TemplatesList
            templates={companyTemplates}
            loading={loading}
            error={error}
          />
        </TemplatesContainer>
      ) : (
        <EmptyStateContainer error={error} loading={loading} />
      )}
      <div ref={ref} style={{ height: '4px' }} />
    </>
  );
};

CompanyTemplates.defaultProps = {
  recentTemplates: [],
};

CompanyTemplates.propTypes = {
  includeAllTemplates: PropTypes.bool.isRequired,
  recentTemplates: PropTypes.arrayOf(PropTypes.object),
};

export default CompanyTemplates;
